import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Copy, Shuffle, Check, Sparkles } from 'lucide-react';
import { transformText } from '@/lib/transformers';
import stylesData from '@/data/styles.json';

interface Style {
  slug: string;
  name: string;
  shortDescription: string;
  unicodeMap: string;
}

interface SurpriseMeModalProps {
  isOpen: boolean;
  onClose: () => void;
  inputText: string;
  onCopy: (text: string, styleName: string) => void;
}

export function SurpriseMeModal({ isOpen, onClose, inputText, onCopy }: SurpriseMeModalProps) {
  const [currentStyle, setCurrentStyle] = useState<Style | null>(null);
  const [transformedText, setTransformedText] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const getRandomStyle = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * stylesData.length);
    return stylesData[randomIndex] as Style;
  }, []);

  const generateRandom = useCallback(() => {
    setIsSpinning(true);
    setCopied(false);
    
    // Small delay for animation effect
    setTimeout(() => {
      const style = getRandomStyle();
      setCurrentStyle(style);
      setTransformedText(transformText(inputText, style.unicodeMap));
      setIsSpinning(false);
    }, 150);
  }, [inputText, getRandomStyle]);

  // Generate initial random style when modal opens
  useEffect(() => {
    if (isOpen && inputText) {
      generateRandom();
    }
  }, [isOpen, inputText, generateRandom]);

  const handleCopy = async () => {
    if (!currentStyle || !transformedText) return;
    
    try {
      await navigator.clipboard.writeText(transformedText);
      setCopied(true);
      onCopy(transformedText, currentStyle.name);
      
      // Reset copied state after delay
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleTryAnother = () => {
    generateRandom();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Surprise Me!
          </DialogTitle>
          <DialogDescription>
            Discover random text styles. Click "Try Another" to explore more options.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Style Name */}
          {currentStyle && (
            <div className="text-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                {currentStyle.name}
              </span>
              <p className="text-xs text-muted-foreground mt-2">
                {currentStyle.shortDescription}
              </p>
            </div>
          )}

          {/* Preview */}
          <div 
            className={`
              min-h-[120px] p-4 rounded-lg border bg-muted/50 
              flex items-center justify-center text-center
              transition-all duration-200
              ${isSpinning ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}
            `}
          >
            <p className="text-lg break-all leading-relaxed">
              {transformedText || 'Loading...'}
            </p>
          </div>

          {/* Original Text Reference */}
          <div className="text-xs text-muted-foreground text-center">
            Original: "{inputText.length > 50 ? inputText.slice(0, 50) + '...' : inputText}"
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleTryAnother}
            disabled={isSpinning}
            className="gap-2 w-full sm:w-auto"
          >
            <Shuffle className={`h-4 w-4 ${isSpinning ? 'animate-spin' : ''}`} />
            Try Another
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!transformedText || isSpinning}
            className="gap-2 w-full sm:w-auto"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy & Close
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
